import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useSearchParams } from "react-router-dom";
import { device } from "../lib/breakpoints";
import { APP_NAME } from "../helpers/constants";
import SEO from "../components/SEO";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email")?.trim() || "";
  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!email) {
      setStatus("error");
      setMessage("No email address was provided in the link.");
      return;
    }

    const baseUrl = process.env.REACT_APP_API_URL;
    if (!baseUrl) {
      setStatus("error");
      setMessage("Unsubscribe is not configured. Please contact support.");
      return;
    }

    const url = `${baseUrl}/marketing/unsubscribe?email=${encodeURIComponent(
      email
    )}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json" } })
      .then(async (res) => {
        const text = await res.text();
        const data = text ? JSON.parse(text) : {};
        if (res.ok && data.success !== false) {
          setStatus("success");
        } else {
          setStatus("error");
          setMessage(
            data?.message ||
              data?.detail ||
              `Request failed (${res.status}). Please try again or contact support.`
          );
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage(
          "We couldn’t complete your request. Please try again or contact support."
        );
      });
  }, [email]);

  return (
    <Container>
      <SEO
        title={`Unsubscribe | ${APP_NAME}`}
        description={`Manage your email preferences for ${APP_NAME}.`}
        keywords={`unsubscribe, email preferences, ${APP_NAME}`}
        imageAlt={`${APP_NAME} unsubscribe`}
        category="Settings"
        tags={["unsubscribe", "email preferences"]}
      />
      <Content>
        {status === "loading" && (
          <>
            <Spinner aria-label="Loading" />
            <Title>Unsubscribing…</Title>
            <Subtitle>Please wait a moment.</Subtitle>
          </>
        )}
        {status === "success" && (
          <>
            <IconWrapper $success>
              <CheckCircleOutlined />
            </IconWrapper>
            <Title>You’re unsubscribed</Title>
            <Subtitle>
              You won’t receive further marketing emails from us. You can still
              get account-related messages.
            </Subtitle>
            <HomeButton to="/">Return to {APP_NAME}</HomeButton>
          </>
        )}
        {status === "error" && (
          <>
            <IconWrapper $error>
              <CloseCircleOutlined />
            </IconWrapper>
            <Title>Something went wrong</Title>
            <Subtitle>{message}</Subtitle>
            <HomeButton to="/">Return to {APP_NAME}</HomeButton>
          </>
        )}
      </Content>
    </Container>
  );
};

export default Unsubscribe;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  padding: 2rem;
`;

const Content = styled.div`
  max-width: 520px;
  text-align: center;
  background: var(--color-bg-secondary);
  padding: 4rem 3rem;
  border-radius: 24px;
  box-shadow: 0 20px 40px var(--shadow-light);
  border: 1.5px solid var(--color-border-gold);
  @media ${device.mobile} {
    padding: 3rem 1.25rem;
  }
`;

const IconWrapper = styled.div`
  font-size: 4rem;
  margin-bottom: 1.25rem;
  color: ${(p) =>
    p.$success ? "var(--color-primary)" : "var(--color-error, #dc3545)"};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 1rem;
  line-height: 1.2;
  font-family: var(--font-primary);
  @media ${device.mobile} {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-light);
  line-height: 1.6;
  margin-bottom: 2rem;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  @media ${device.mobile} {
    font-size: 1rem;
  }
`;

const HomeButton = styled(Link)`
  display: inline-block;
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  font-family: var(--font-secondary);
  box-shadow: 0 4px 15px rgba(44, 95, 45, 0.3);
  &:hover {
    background: var(--gradient-primary-dark);
    transform: scale(1.03);
    color: white;
  }
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 1.5rem;
  border: 4px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
